import { IStreamer } from 'constants/types';
import { listStreamers } from 'lib/api/streamers';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type StreamersPaginationOptions = {
  filters?: Record<string, unknown>;
  initialData?: IStreamer[];
  initialCount?: number;
};

export const useStreamersPagination = ({
  filters = {},
  initialData = [],
  initialCount,
}: StreamersPaginationOptions = {}) => {
  const limit = 12;
  const filtersKey = JSON.stringify(filters);
  const resolvedInitialCount =
    typeof initialCount === 'number' ? initialCount : initialData.length;

  const [data, setData] = useState<IStreamer[]>(initialData);
  const [count, setCount] = useState(
    resolvedInitialCount > 0 ? resolvedInitialCount : -1
  );
  const [isFetching, setIsFetching] = useState(false);
  const [page, setPage] = useState(1);
  const initialFiltersKeyRef = useRef(filtersKey);
  const skipInitialFetchRef = useRef(
    initialData.length > 0 &&
      initialData.length >= Math.min(initialData.length, limit)
  );

  const refetchData = useCallback(() => {
    setPage(1);
    setData([]);
    setCount(-1);
    skipInitialFetchRef.current = false;
  }, []);

  useEffect(() => {
    setData(initialData);
    setCount(
      resolvedInitialCount > 0 ? resolvedInitialCount : initialData.length
    );
    setPage(1);
    initialFiltersKeyRef.current = filtersKey;
    skipInitialFetchRef.current =
      initialData.length > 0 &&
      initialData.length >= Math.min(initialData.length, limit);
  }, [initialData, initialCount, resolvedInitialCount, filtersKey, limit]);

  useEffect(() => {
    const shouldUseInitialData =
      page === 1 &&
      skipInitialFetchRef.current &&
      filtersKey === initialFiltersKeyRef.current;

    if (shouldUseInitialData) {
      return undefined;
    }

    let isActive = true;

    setIsFetching(true);
    listStreamers({ page, limit, ...filters })
      .then((response) => {
        if (!isActive) {
          return;
        }

        const results = response.streamers ?? [];
        setData((current) => (page === 1 ? results : [...current, ...results]));
        setCount(response.count ?? results.length);
      })
      .finally(() => {
        if (isActive) {
          setIsFetching(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [filtersKey, page, filters, limit]);

  const isLast = useMemo(() => {
    if (count === -1) {
      return false;
    }

    return data.length >= count;
  }, [count, data.length]);

  return {
    page,
    data,
    isLast,
    setPage,
    isFetching,
    count: count < 0 ? 0 : count,
    refetchData,
  };
};
